import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/context/AuthContext";
import { Avatar } from "@/shared/components/ui/Avatar";
import { useAttachmentUpload } from "@/shared/hooks/useAttachmentUpload";
import { getAttachmentViewingUrl } from "@/shared/services/uploadService";
import api from "@/shared/services/apiClient";
import { changeLanguageAndPersist } from "@/i18n";
import { formatDisplayName } from "@/shared/utils/formatters";
import { notificationService } from "@/features/profile/services/visaService";
import { SUPPORTED_LANGUAGES, LANGUAGE_FLAGS, LANGUAGE_NATIVE_NAMES } from "@/features/chat/services/translationService";

const OFFICER_TYPES = ["officer", "admin"];

export const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { userData, userToken, streamToken, roles, login } = useAuth();
  const isOfficer = OFFICER_TYPES.includes(
    userData?.userType?.toLowerCase() || "",
  );
  const isSouser = userData?.userType?.toLowerCase() === "souser";

  const { upload, isUploading, progress } = useAttachmentUpload();
  const [profilePic, setProfilePic] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [notifEnabled, setNotifEnabled] = useState<boolean>(
    userData?.notification ?? true,
  );
  const [autoTranslateEnabled, setAutoTranslateEnabled] = useState<boolean>(
    userData?.auto_translate_chat ?? false,
  );
  const [translateExceptions, setTranslateExceptions] = useState<string[]>(
    userData?.translate_exceptions ?? [],
  );

  const menuItems = [
    ...(!isSouser
      ? [
          {
            to: "/settings/personal-info",
            label: t("settings.personal_info"),
            icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
          },
        ]
      : [
          {
            to: "/settings/souser-profile",
            label: t("souser.my_profile"),
            icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
          },
        ]),
    ...(!isOfficer && !isSouser
      ? [
          {
            to: "/settings/visa-info",
            label: t("settings.work_visa_info"),
            icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
          },
        ]
      : []),
    {
      to: "/settings/reset-password",
      label: t("settings.change_password"),
      icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
    },
  ];

  // Load profile picture
  useEffect(() => {
    const loadProfilePicture = async () => {
      if (userData?.profilePicId) {
        try {
          const url = await getAttachmentViewingUrl(userData.profilePicId);
          setProfilePic(url);
        } catch (error) {
          console.error("Error loading profile picture:", error);
          setProfilePic(null);
        }
      } else {
        setProfilePic(null);
      }
    };
    loadProfilePicture();
  }, [userData?.profilePicId]);

  // Delete old profile picture
  const deleteOldProfilePicture = async (userId: string | number) => {
    try {
      await api.delete(`/attachments/profile/${userId}`);
    } catch (error: any) {
      if (error?.response?.status === 404) return;
      console.error("Error deleting old profile picture:", error);
    }
  };

  // Update user data with new profile pic ID
  const updateUserProfilePicId = (newProfilePicId: number | string) => {
    if (!userData || !userToken) return;

    // Update userData with new profilePicId
    const updatedUser = {
      ...userData,
      profilePicId: String(newProfilePicId),
    };

    login(userToken, streamToken || "", updatedUser, roles);
  };

  // Handle file selection
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !userData?.id) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert(t("common.error") + ": " + t("profile.invalid_file_type"));
      return;
    }

    try {
      // Delete old profile picture
      await deleteOldProfilePicture(userData.id);

      // Upload new profile picture
      const result = await upload(
        file,
        "profile",
        userData.id,
        {
          relation_type: "profile",
          relation_id: String(userData.id),
        }
      );

      if (result?.attachment_id) {
        // Set profile picture immediately
        const newProfileUrl = await getAttachmentViewingUrl(
          result.attachment_id,
        );
        setProfilePic(newProfileUrl);

        // Update user data in auth context with new profilePicId
        updateUserProfilePicId(result.attachment_id);

        alert(t("common.success") + ": " + t("profile.upload_success"));
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert(t("profile.upload_failed") + ": " + t("profile.upload_error"));
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Trigger file input click
  const handleAvatarClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  // Delete profile picture
  const handleDeleteProfilePic = async () => {
    if (!userData?.id || !userData?.profilePicId) return;

    const confirmed = window.confirm(
      t("profile.delete_confirmation") ||
        "Are you sure you want to remove your profile picture?",
    );

    if (!confirmed) return;

    try {
      // Delete the profile picture
      await deleteOldProfilePicture(userData.id);

      // Clear the profile picture from state
      setProfilePic(null);

      // Update user data to remove profilePicId
      if (userToken) {
        const updatedUser = {
          ...userData,
          profilePicId: undefined,
        };
        login(userToken, streamToken || "", updatedUser, roles);
      }

      alert(
        t("common.success") +
          ": " +
          (t("profile.delete_success") ||
            "Profile picture removed successfully"),
      );
    } catch (error) {
      console.error("Delete error:", error);
      alert(
        t("common.error") +
          ": " +
          (t("profile.delete_failed") || "Failed to remove profile picture"),
      );
    }
  };

  // Handle notification toggle
  const handleNotificationToggle = async () => {
    const newValue = !notifEnabled;
    setNotifEnabled(newValue);
    try {
      await notificationService.updateNotificationPreference(newValue);
      if (userData && userToken) {
        login(userToken, streamToken || "", { ...userData, notification: newValue }, roles);
      }
    } catch (error) {
      console.error("Failed to update notification preference:", error);
      setNotifEnabled(!newValue); // revert on error
    }
  };

  // Handle auto-translate toggle
  const handleAutoTranslateToggle = async () => {
    const newValue = !autoTranslateEnabled;
    setAutoTranslateEnabled(newValue);
    try {
      await api.patch("/profile/update-auto-translate-chat", { enabled: newValue });
      if (userData && userToken) {
        login(userToken, streamToken || "", { ...userData, auto_translate_chat: newValue }, roles);
      }
    } catch (error) {
      console.error("Failed to update auto-translate preference:", error);
      setAutoTranslateEnabled(!newValue); // revert on error
    }
  };

  const handleExceptionToggle = useCallback(async (code: string) => {
    const next = translateExceptions.includes(code)
      ? translateExceptions.filter((c) => c !== code)
      : [...translateExceptions, code];
    setTranslateExceptions(next);
    try {
      await api.patch("/profile/update-translate-exceptions", { exceptions: next });
      if (userData && userToken) {
        login(userToken, streamToken || "", { ...userData, translate_exceptions: next }, roles);
      }
    } catch (error) {
      console.error("Failed to update translate exceptions:", error);
      setTranslateExceptions(translateExceptions); // revert on error
    }
  }, [translateExceptions, userData, userToken, streamToken, roles, login]);

  // Handle language change
  const handleLanguageChange = async (newLanguage: string) => {
    // Change language in UI immediately and persist to localStorage
    await changeLanguageAndPersist(newLanguage);

    // Sync with backend
    try {
      await api.patch("/profile/update-language", { language: newLanguage });
      console.log("✅ Language preference saved to backend");

      // Update auth context with new language preference
      if (userData && userToken) {
        const updatedUser = {
          ...userData,
          preferred_language: newLanguage,
        };
        login(userToken, streamToken || "", updatedUser, roles);
      }
    } catch (error) {
      console.error("Failed to save language preference to backend:", error);
      // UI already switched, so we continue even if backend sync fails
    }
  };

  return (
    <div>
      <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
        {t("settings.title")}
      </h1>

      {/* Profile card */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6 mb-4 md:mb-6 flex flex-col items-center gap-3 md:flex-row md:items-center md:gap-4">
        <div className="relative">
          <div
            className={`cursor-pointer ${isUploading ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"} transition-opacity`}
            onClick={handleAvatarClick}
          >
            <Avatar
              firstName={userData?.firstName}
              lastName={userData?.lastName}
              src={profilePic || undefined}
              size="lg"
            />
          </div>

          {/* Camera icon overlay */}
          <button
            onClick={handleAvatarClick}
            disabled={isUploading}
            className="absolute bottom-0 right-0 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-full p-2 shadow-lg transition-colors"
            aria-label="Upload profile picture"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>

          {/* Delete button - only show if there's a profile picture */}
          {profilePic && userData?.profilePicId && (
            <button
              onClick={handleDeleteProfilePic}
              disabled={isUploading}
              className="absolute top-0 right-0 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-full p-1.5 shadow-lg transition-colors"
              aria-label="Remove profile picture"
            >
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}

          {/* Upload progress overlay */}
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
              <div className="text-white text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-1"></div>
                <span className="text-xs font-semibold">
                  {Math.round(progress)}%
                </span>
              </div>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        <div className="text-center md:text-left">
          <h2 className="text-lg font-semibold">
            {formatDisplayName(userData?.lastName, userData?.firstName, userData?.middleName)}
          </h2>
          <p className="text-sm text-gray-500">{userData?.email}</p>
          <p className="text-xs text-gray-400 mt-1">
            {userData?.company_name} &middot; {isSouser ? t("settings.user_type_souser") : userData?.userType}
          </p>
        </div>
      </div>

      {/* Menu */}
      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
        <div className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {t("settings.section_account")}
        </div>
        {menuItems.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="flex items-center gap-3 px-4 py-4 md:py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors"
          >
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={item.icon}
              />
            </svg>
            <span className="text-sm text-gray-700">{item.label}</span>
          </Link>
        ))}
      </div>

      {/* Language */}
      <div className="bg-white rounded-lg border border-gray-200 mt-4 p-4">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
          {t("settings.section_preferences")}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">
            {t("settings.language")}
          </span>
          <select
            value={i18n.language}
            onChange={(e) => handleLanguageChange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="en">English</option>
            <option value="ja">日本語</option>
            <option value="vi">Tiếng Việt</option>
            <option value="id">Bahasa Indonesia</option>
            <option value="th">ภาษาไทย</option>
            <option value="km">ភាសាខ្មែរ</option>
            <option value="bn">বাংলা</option>
            <option value="my">မြန်မာဘာသာ</option>
            <option value="ne">नेपाली</option>
          </select>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div>
            <span className="text-sm text-gray-700">
              {t("settings.notifications")}
            </span>
            <p className="text-xs text-gray-400 mt-0.5">
              {t("settings.notifications_desc")}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={notifEnabled}
            onClick={handleNotificationToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
              notifEnabled ? "bg-indigo-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                notifEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div>
            <span className="text-sm text-gray-700">
              {t("settings.auto_translate_chat")}
            </span>
            <p className="text-xs text-gray-400 mt-0.5">
              {t("settings.auto_translate_chat_desc")}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={autoTranslateEnabled}
            onClick={handleAutoTranslateToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
              autoTranslateEnabled ? "bg-indigo-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                autoTranslateEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Translate exceptions — shown only when auto-translate is on */}
        {autoTranslateEnabled && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <span className="text-sm text-gray-700">
              {t("settings.translate_exceptions")}
            </span>
            <p className="text-xs text-gray-400 mt-0.5 mb-3">
              {t("settings.translate_exceptions_desc")}
            </p>
            <div className="flex flex-wrap gap-2">
              {SUPPORTED_LANGUAGES.map((lang) => {
                const isException = translateExceptions.includes(lang.code);
                return (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => handleExceptionToggle(lang.code)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      isException
                        ? "bg-indigo-600 border-indigo-600 text-white"
                        : "bg-white border-gray-300 text-gray-600 hover:border-indigo-400"
                    }`}
                  >
                    <span>{LANGUAGE_FLAGS[lang.code]}</span>
                    <span>{LANGUAGE_NATIVE_NAMES[lang.code]}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
